import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { LozengeSelectFilter } from '../Filter/LozengeSelectFilter';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import { useMapData } from '../../map-data/use-map-data';
import { muiTheme } from '../../styles/theme';
import { hexToRgb } from '../../utils/colour-utils';
import type { MultiChoiceToggleFilterConfig } from '../Filter/filter-types';
import { countryStatusConfig } from '../../map-data/heatmap-legend-config';

export const HeatMapStatusFilter: FC = () => {
  const { t } = useTranslation();
  const { updateFilterOptions, filterOptions } = useHeatMapFilterOptions();
  const { countryHeatMapMetaData } = useMapData();

  // Only show filter buttons for active statuses that exist in the data
  const relevantStatuses = useMemo(() => {
    if (!countryHeatMapMetaData) return [];
    const statusesInData = new Set<string>(
      Object.values(countryHeatMapMetaData).map(c => c.countryStatus),
    );
    return countryStatusConfig.filter(
      s => s.active && statusesInData.has(s.dataValue),
    );
  }, [countryHeatMapMetaData]);

  const statusFilterConfig: MultiChoiceToggleFilterConfig = {
    icon: 'StudyDesignCheck',
    title: '',
    key: 'status',
    subtitle: '',
    width: 'full',
    keyOptions: [
      {
        label: t('heatMapExperience.filters.options.seeAll'),
        value: 'AllStatuses',
        color: '255, 255, 255',
        textColor: muiTheme.palette.text.primary,
      },
      ...relevantStatuses.map(s => ({
        value: s.key,
        label: t(`countryStatus.${s.translationKey}`),
        color: hexToRgb(s.color),
        textColor: s.textColor,
        normalColor: hexToRgb(s.colorLight),
        hoverColor: hexToRgb(s.colorLighter),
      })),
    ],
    onUpdate: updateFilterOptions,
    filterType: 'multiChoiceToggle',
  };

  // Get the current status filter value
  const currentStatus = filterOptions.status;
  const selectedValues = Array.isArray(currentStatus) ? currentStatus : [];

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        right: 8,
        display: 'flex',
        gap: 1,
        zIndex: 1,
      }}
    >
      <LozengeSelectFilter
        filterConfig={statusFilterConfig}
        selectedValues={selectedValues}
        isMultiSelect={true}
        useOutlineStyle={true}
        compactMode={true}
        clearValue="AllStatuses"
      />
    </Box>
  );
};
